import { useContext } from 'react';
import { MutationFunction, useMutation, useQueryClient } from '@tanstack/react-query';
import axios, { AxiosResponse } from 'axios';
import { AmplitudeFlagKeys } from 'types/amplitudeFlags';
import { captureException } from 'utils/exception';

import { OrganizationContext } from 'components/AdminNew/Companies/CompanyDetails/OrganizationContext';
import useIsExperimentActive from 'components/Amplitude/useIsExperimentActive';
import { Codeowners } from 'components/Common/Error/Codeowners';
import { useAppMessages } from 'components/Common/hooks/useAppMessages';
import { useToastController } from 'components/Common/Toast';
import { TriggerContext } from 'components/Settings/Billing/types';

import CreditCardsAdminApi, { AddCreditCardResponse } from '../api/CreditCardsAdminApi';
import CreditCardsProApi from '../api/CreditCardsProApi';
import { ErrorResponse } from '../types';

export type UseAddCreditCardVariables = {
  orgId?: number;
  stripe_card_token: string;
  primary: boolean;
};

const addCreditCardAdminMutation = (variables: UseAddCreditCardVariables, source: string, useUUIDs = false) => {
  if (!variables.orgId) {
    throw new Error('No organization id provided');
  }

  return new CreditCardsAdminApi(useUUIDs).add(
    variables.orgId,
    {
      stripe_card_token: variables.stripe_card_token,
      primary: variables.primary,
    },
    source
  );
};

const addCreditCardProMutation = (variables: UseAddCreditCardVariables, source: string, useUUIDs = false) => {
  return new CreditCardsProApi(useUUIDs).add(
    {
      stripe_card_token: variables.stripe_card_token,
      primary: variables.primary,
    },
    source
  );
};

const useAddCreditCard = (source: string) => {
  const queryClient = useQueryClient();
  const { showToast } = useToastController();
  const useCreditCardsUUIDs = useIsExperimentActive(AmplitudeFlagKeys.billingEnableCreditCardsUUIDs);
  const { message } = useAppMessages();

  const { triggerContext: context } = useContext(OrganizationContext);

  let addCreditCardMutationMutation: MutationFunction<AxiosResponse<AddCreditCardResponse>, UseAddCreditCardVariables>;

  switch (context) {
    case TriggerContext.AdminPanel:
      addCreditCardMutationMutation = (variables: UseAddCreditCardVariables) =>
        addCreditCardAdminMutation(variables, source, useCreditCardsUUIDs);
      break;
    case TriggerContext.ProDashboard:
      addCreditCardMutationMutation = (variables: UseAddCreditCardVariables) =>
        addCreditCardProMutation(variables, source, useCreditCardsUUIDs);
      break;
    default:
      throw new Error('Invalid context provided for useAddCreditCard hook');
  }

  return useMutation(addCreditCardMutationMutation, {
    onSuccess: (response, variables) => {
      queryClient.invalidateQueries(['creditCards', variables.orgId]);
      showToast(response.data.message, { variant: 'success' });
    },
    onError: (error: ErrorResponse) => {
      showToast(error.response?.data?.error ?? message.error.charging.addCreditCardFailed);

      if (!axios.isAxiosError(error)) {
        captureException(error, Codeowners.BILLING);
      }
    },
  });
};

export default useAddCreditCard;
